/**
 * status-bar.tsx — Thanh trạng thái trên cùng màn nhân viên AI (từ GET /ai/bots/:id/status):
 *   "Đang trả lời khách · N kênh · <chế độ lúc này> · bản phát hành vN" hoặc "Chưa trả lời khách: <lý do chặn đầu tiên>"
 *   + nút sửa theo `fix` (đường dẫn ngoài → mở màn đó ở TAB MỚI, màn này giữ nguyên; mục của màn này → mở mục).
 *   "Xem chi tiết" hiện mọi lý do. Có nháp chưa phát hành → nhắc + nút Phát hành. Nút "Tải lại" kiểm tra lại trạng thái
 *   (cũng tự kiểm tra lại khi quay lại tab).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Rocket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import type { AiBotStatus } from '@/hooks/use-ai'
import { cn } from '@/lib/utils'
import { firstBlocking, fixAction, statusHeadline, type StatusReason, type StatusSection } from '@/components/ai/bot-status-lib'

export function StaffStatusBar({ botId, status, loading, error, onReload, onSection, onPublish, canEdit }: {
  botId: string
  status: AiBotStatus | undefined
  loading: boolean
  error: boolean
  /** Kiểm tra lại trạng thái (GET /ai/bots/:id/status). */
  onReload: () => Promise<unknown>
  onSection: (s: Exclude<StatusSection, 'phat-hanh'>) => void
  onPublish: () => void
  canEdit: boolean
}) {
  const { t } = useTranslation('aiStaff')
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  if (loading && !status) {
    return <div className="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('statusBar.checking')}</div>
  }
  if (error || !status) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border px-4 py-2.5 text-sm text-muted-foreground">
        <span className="min-w-0 flex-1">{t('statusBar.checkFailed')}</span>
        <ReloadButton onReload={onReload} what={t('statusBar.reloadWhat')} />
      </div>
    )
  }

  const blocking = firstBlocking(status)
  const reasons = status.reasons ?? []
  const unpublished = !!status.hasUnpublishedChanges

  const run = (r: StatusReason) => {
    const a = fixAction(r.fix, botId)
    if (a.kind === 'section') onSection(a.section)
    else if (a.kind === 'publish') onPublish()
    else if (a.kind === 'navigate') navigate(a.path)
  }
  const fixButton = (r: StatusReason, primary = false) => {
    const a = fixAction(r.fix, botId)
    if (a.kind === 'none' || !r.fix) return r.fix?.label ? <span className="text-xs text-muted-foreground">{r.fix.label}</span> : null
    if (a.kind === 'publish' && !canEdit) return null
    if (a.kind === 'navigate' && a.newTab) {
      return <OpenInNewTab to={a.path} variant={primary ? 'default' : 'outline'} size="sm" className="h-7 shrink-0">{r.fix.label}</OpenInNewTab>
    }
    return (
      <Button size="sm" variant={primary ? 'default' : 'outline'} className="h-7 shrink-0" onClick={() => run(r)}>
        {r.fix.label}
      </Button>
    )
  }

  return (
    <div className={cn('rounded-xl border px-4 py-2.5', status.answering ? 'border-success/30 bg-success/5' : 'border-warning/40 bg-warning/10')} role="status">
      <div className="flex flex-wrap items-center gap-2">
        {status.answering
          ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
          : <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />}
        <p className="min-w-0 flex-1 text-sm font-medium">{statusHeadline(status)}</p>
        {blocking && fixButton(blocking, true)}
        {unpublished && canEdit && (
          <Button size="sm" variant={blocking ? 'outline' : 'default'} className="h-7" onClick={onPublish}>
            <Rocket className="h-3.5 w-3.5" /> {t('statusBar.publish')}
          </Button>
        )}
        <ReloadButton onReload={onReload} what={t('statusBar.reloadWhat')} iconOnly />
        {reasons.length > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" aria-expanded={open}>
            {open ? t('statusBar.hideDetails') : t('statusBar.showDetails', { count: reasons.length })}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>
      {unpublished && (
        <p className="mt-1 pl-6 text-xs text-muted-foreground">
          {t('statusBar.unpublished', { target: status.publishedVersion != null ? t('statusBar.versionTarget', { version: status.publishedVersion }) : t('statusBar.currentConfig') })}
        </p>
      )}
      {open && (
        <ul className="mt-2 space-y-1.5 border-t pt-2">
          {reasons.map((r) => (
            <li key={r.code} className="flex flex-wrap items-center gap-2 text-xs">
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', r.blocking === false ? 'bg-muted-foreground/50' : 'bg-warning')} />
              <span className="min-w-0 flex-1">{r.message}{r.blocking === false && <span className="text-muted-foreground">{t('statusBar.nonBlocking')}</span>}</span>
              {fixButton(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
